
<template>
    <div class="token-header-compact">
        <!-- Single row: Logo, Name/Price, Stats -->
        <div class="header-main">
            <div class="header-identity">
                <img v-if="tokenData.logoURI" :src="tokenData.logoURI" :alt="`${tokenData.symbol} logo`" class="token-logo">
                <div v-else class="token-logo token-avatar-placeholder">
                    <span class="avatar-letter">{{ tokenData.symbol ? tokenData.symbol.charAt(0).toUpperCase() : '?' }}</span>
                </div>
                <div class="header-left">
                    <h1 class="token-name">{{ tokenData.name }} <span class="token-symbol">({{ tokenData.symbol }})</span></h1>
                    <div class="price-row">
                        <span :class="['token-price', priceFlashClass]">${{ formattedPrice }}</span>
                        <span :class="changeClass" class="token-change">({{ formattedChange }}%)</span>
                        <pro-mode-toggle></pro-mode-toggle>
                    </div>
                </div>
            </div>
            <div class="header-stats">
                <div class="stat-item">
                    <span class="stat-label">Volume</span>
                    <span class="stat-value">${{ formatNumber(tokenData.volume_24h) }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">MCap</span>
                    <span class="stat-value">${{ formatNumber(tokenData.market_cap) }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Liquidity</span>
                    <span class="stat-value">${{ formatNumber(displayedLiquidity) }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Supply</span>
                    <span class="stat-value">{{ formatNumber(tokenData.circulating_supply) }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Holders</span>
                    <span class="stat-value">{{ formatNumber(tokenData.holder_count) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
// Assuming CopyButton and AddToWatchlistButton components exist and are registered globally
// import CopyButton from './CopyButton.vue';
// import AddToWatchlistButton from './AddToWatchlistButton.vue';

const props = defineProps({
    initialToken: {
        type: Object,
        required: true,
    }
});

const tokenData = ref(props.initialToken);
const previousPrice = ref(props.initialToken?.latest_price || 0);
const priceFlashClass = ref('');
const liquidityFromPools = ref(null); // Updated by TokenMarkets component
let intervalId = null;
let echoChannel = null;

// Listen for liquidity updates from TokenMarkets
const handleLiquidityUpdate = (event) => {
    if (event.detail.mint === tokenData.value.mint) {
        liquidityFromPools.value = event.detail.displayedLiquidity;
    }
};

// Display liquidity - prefer pool-calculated value
const displayedLiquidity = computed(() => {
    if (liquidityFromPools.value !== null) {
        return liquidityFromPools.value;
    }
    return tokenData.value.liquidity_usd || 0;
});

// --- Formatting Helpers (move to a utility file later) ---
const formatPrice = (price) => {
    if (price === 0 || !price) return '0.00';
    if (price > 1) return price.toFixed(2);
    if (price > 0.01) return price.toFixed(4);
    // Find the first non-zero digit after the decimal point
    const priceStr = price.toString();
    const decimalPart = priceStr.split('.')[1];
    if (!decimalPart) return price.toFixed(2); // Should not happen for < 1 but safety check

    let firstSignificantDigitIndex = -1;
    for (let i = 0; i < decimalPart.length; i++) {
        if (decimalPart[i] !== '0') {
            firstSignificantDigitIndex = i;
            break;
        }
    }

    if (firstSignificantDigitIndex === -1) return '0.00'; // All zeros

    // Show 3 significant digits after leading zeros
    const significantDigits = 3;
    const totalDecimalPlaces = firstSignificantDigitIndex + significantDigits;

    return price.toFixed(totalDecimalPlaces);
};

const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toFixed(2); // Show 2 decimals for numbers < 1000
};
// --- End Formatting Helpers ---


const formattedPrice = computed(() => formatPrice(tokenData.value.latest_price));
const formattedChange = computed(() => tokenData.value.change_24h.toFixed(2));

const changeClass = computed(() => {
    return tokenData.value.change_24h >= 0 ? 'text-success' : 'text-danger';
});

// --- Placeholder Data/Calculations (Replace with actual data fetching/logic) ---
const marketCap = computed(() => 0); // TODO: Calculate Market Cap (Price * Circulating Supply)
const circulatingSupply = computed(() => 0); // TODO: Fetch/Calculate Circulating Supply
const holderCount = computed(() => 0); // TODO: Fetch Holder Count
// --- End Placeholder Data ---

const fetchUpdate = async () => {
    try {
        // Fetch stats (volume, change, market cap) from OHLCV transform
        const priceResponse = await fetch(`/api/tokens/${tokenData.value.mint}/price`);
        if (!priceResponse.ok) throw new Error('Failed to fetch price update');
        const priceData = await priceResponse.json();

        // Try live price (Redis VWAP → ES fallback) for more current value
        let livePrice = null;
        try {
            const liveResp = await fetch(`/api/tokens/live-prices?mints=${tokenData.value.mint}`);
            if (liveResp.ok) {
                const liveData = await liveResp.json();
                livePrice = liveData.data?.[tokenData.value.mint]?.price_usd;
            }
        } catch {}

        const newPrice = livePrice || priceData.price_usd || tokenData.value.latest_price;
        const oldPrice = tokenData.value.latest_price;

        // Visual feedback: flash green if price went up, red if down
        if (newPrice !== oldPrice && oldPrice > 0) {
            priceFlashClass.value = newPrice > oldPrice ? 'price-flash-up' : 'price-flash-down';
            setTimeout(() => {
                priceFlashClass.value = '';
            }, 1000);
        }

        // Update reactive ref - Vue handles the rest
        tokenData.value = {
            ...tokenData.value, // Keep existing static data
            latest_price: newPrice,
            change_24h: priceData.change_24h ?? tokenData.value.change_24h,
            volume_24h: priceData.volume_24h ?? tokenData.value.volume_24h,
            market_cap: priceData.market_cap ?? tokenData.value.market_cap,
            liquidity_usd: priceData.liquidity_usd ?? tokenData.value.liquidity_usd,
        };

        // Update SSR SEO stats block (progressive enhancement)
        updateSeoStatsBlock();
    } catch (error) {
        console.error(`Error fetching updates for ${tokenData.value.mint}:`, error);
    }
};

// Update the SSR SEO stats block with live data (progressive enhancement pattern)
const updateSeoStatsBlock = () => {
    const mint = tokenData.value.mint;

    // Update price
    const priceEl = document.querySelector(`[data-mint="${mint}"][data-field="price"]`);
    if (priceEl) {
        priceEl.textContent = `$${formatPrice(tokenData.value.latest_price)}`;
    }

    // Update change with color class
    const changeEl = document.querySelector(`[data-mint="${mint}"][data-field="change"]`);
    if (changeEl) {
        const change = tokenData.value.change_24h;
        changeEl.textContent = `${change.toFixed(2)}%`;
        changeEl.classList.remove('pct-positive', 'pct-negative', 'text-success', 'text-danger');
        changeEl.classList.add(change >= 0 ? 'pct-positive' : 'pct-negative');
    }

    // Update volume
    const volumeEl = document.querySelector(`[data-mint="${mint}"][data-field="volume"]`);
    if (volumeEl) {
        volumeEl.textContent = `$${formatNumber(tokenData.value.volume_24h)}`;
    }

    // Update market cap
    const mcapEl = document.querySelector(`[data-mint="${mint}"][data-field="mcap"]`);
    if (mcapEl) {
        mcapEl.textContent = `$${formatNumber(tokenData.value.market_cap)}`;
    }

    // Update liquidity
    const liqEl = document.querySelector(`[data-mint="${mint}"][data-field="liquidity"]`);
    if (liqEl) {
        liqEl.textContent = `$${formatNumber(displayedLiquidity.value)}`;
    }

    // Update holders (usually doesn't change often, but update anyway)
    const holdersEl = document.querySelector(`[data-mint="${mint}"][data-field="holders"]`);
    if (holdersEl && tokenData.value.holder_count) {
        holdersEl.textContent = formatNumber(tokenData.value.holder_count);
    }
};

/**
 * Handle a live trade from WebSocket — update price with flash animation.
 */
const handleLiveTrade = (trade) => {
    const SOL_MINT = 'So11111111111111111111111111111111111111112';
    let priceUsd = 0;

    if (trade.token_a && trade.token_b) {
        if (trade.token_a.id === tokenData.value.mint) {
            priceUsd = trade.token_a.price_usd / 1e12;
        } else if (trade.token_b.id === tokenData.value.mint) {
            priceUsd = trade.token_b.price_usd / 1e12;
        } else {
            return;
        }
    }

    if (priceUsd <= 0) return;

    const oldPrice = tokenData.value.latest_price;
    if (priceUsd !== oldPrice && oldPrice > 0) {
        priceFlashClass.value = priceUsd > oldPrice ? 'price-flash-up' : 'price-flash-down';
        setTimeout(() => { priceFlashClass.value = ''; }, 1000);
    }

    tokenData.value = {
        ...tokenData.value,
        latest_price: priceUsd,
    };

    updateSeoStatsBlock();
};

/**
 * Handle a live market update from WebSocket — update liquidity/reserves.
 */
const handleMarketUpdate = (market) => {
    if (market.tvl_usd != null) {
        // tvl_usd from Redis PubSub is in picoUSD (1e12 scale)
        tokenData.value = {
            ...tokenData.value,
            liquidity_usd: market.tvl_usd / 1e12,
        };
        updateSeoStatsBlock();
    }
};

/**
 * Subscribe to Echo channel for live updates.
 */
const subscribeToLiveUpdates = () => {
    if (!window.Echo) return;
    unsubscribeFromLiveUpdates();

    echoChannel = window.Echo.channel(`token.${tokenData.value.mint}`);
    echoChannel.listen('.trade.executed', handleLiveTrade);
    echoChannel.listen('.market.updated', handleMarketUpdate);
};

const unsubscribeFromLiveUpdates = () => {
    if (echoChannel) {
        window.Echo.leave(`token.${tokenData.value.mint}`);
        echoChannel = null;
    }
};

onMounted(() => {
    fetchUpdate(); // Fetch latest data on mount
    intervalId = setInterval(fetchUpdate, 30000); // Poll every 30s as fallback (WS is primary)
    // Listen for liquidity updates from TokenMarkets
    window.addEventListener('tokenLiquidityUpdate', handleLiquidityUpdate);
    // Subscribe to live WebSocket updates
    subscribeToLiveUpdates();
});

onUnmounted(() => {
    clearInterval(intervalId);
    unsubscribeFromLiveUpdates();
    window.removeEventListener('tokenLiquidityUpdate', handleLiquidityUpdate);
});

</script>

<style scoped>
/* Compact header - everything on one row */
.token-header-compact {
    margin-bottom: 0.5rem;
}

/* Main header row: logo + name/price + stats all horizontal */
.header-main {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

/* Keep logo + name/price together, never wrap between them */
.header-identity {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
}

.token-logo {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    flex-shrink: 0;
}

.header-left {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
}

/* Token name with gradient text - compact */
.token-name {
    font-size: 1.1rem;
    margin: 0;
    background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    line-height: 1.1;
    white-space: nowrap;
}

.token-symbol {
    background: linear-gradient(135deg, #7c3aed, #a855f7);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 500;
    opacity: 0.85;
}

.price-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

/* Price with monospace - compact */
.token-price {
    font-family: 'JetBrains Mono', 'SF Mono', 'Monaco', monospace;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--compass-text-primary, #e6edf3);
}

/* Price flash animation */
.price-flash-up {
    animation: flash-green 1s ease-out;
}

.price-flash-down {
    animation: flash-red 1s ease-out;
}

@keyframes flash-green {
    0% {
        background-color: rgba(34, 197, 94, 0.4);
        border-radius: 4px;
    }
    100% {
        background-color: transparent;
    }
}

@keyframes flash-red {
    0% {
        background-color: rgba(239, 68, 68, 0.4);
        border-radius: 4px;
    }
    100% {
        background-color: transparent;
    }
}

/* Change percentage styling */
.token-change {
    font-family: 'JetBrains Mono', 'SF Mono', 'Monaco', monospace;
    font-size: 0.85rem;
    font-weight: 600;
}

/* Stats row - to the right with bubble styling */
.header-stats {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
    flex-wrap: wrap;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.1rem;
    padding: 0.35rem 0.6rem;
    background: rgba(139, 92, 246, 0.06);
    border: 1px solid rgba(139, 92, 246, 0.12);
    border-radius: 8px;
    min-width: 55px;
}

.stat-label {
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--compass-text-secondary, #7d8590);
    line-height: 1;
}

.stat-value {
    font-family: 'JetBrains Mono', 'SF Mono', 'Monaco', monospace;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
    line-height: 1.2;
}

:global(.dark-mode) .stat-value {
    color: #e6edf3;
}

body:not(.dark-mode) .stat-item {
    background: rgba(139, 92, 246, 0.04);
    border-color: rgba(139, 92, 246, 0.1);
}

body:not(.dark-mode) .stat-value {
    color: #1f2937;
}

body:not(.dark-mode) .token-price {
    color: #1f2937;
}

/* Avatar placeholder */
.token-avatar-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.2));
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.3));
}

.avatar-letter {
    font-size: 1rem;
    font-weight: 600;
    background: var(--compass-gradient, linear-gradient(135deg, #8b5cf6, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Mobile: stack stats below */
@media (max-width: 768px) {
    .header-main {
        flex-wrap: wrap;
    }

    .header-identity {
        gap: 0.5rem;
    }

    .token-logo {
        width: 32px;
        height: 32px;
    }

    .token-name {
        font-size: 0.95rem;
    }

    .token-price {
        font-size: 1.1rem;
    }

    .token-change {
        font-size: 0.75rem;
    }

    .header-stats {
        width: 100%;
        margin-left: 0;
        justify-content: space-between;
        border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
        padding-top: 0.5rem;
        margin-top: 0.25rem;
    }

    .stat-item {
        align-items: center;
    }

    .stat-label {
        font-size: 0.6rem;
    }

    .stat-value {
        font-size: 0.7rem;
    }

    .avatar-letter {
        font-size: 0.85rem;
    }
}
</style>
